function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { countBy, isEmpty, mapValues } from 'lodash-es';
import { PusherEventName } from 'anchor-scout-common/types';
import { TableList, Page, PageEmptyState, PageErrorState, FilterPickerGroup, Button, Typography, Searchbox, PageContentHeader, ConfirmationModal, DropdownMenu, ChipButton, useStateInRef } from 'seagull';
import { MailSentLiner24, WideArrowDownward, WideArrowUpward } from 'seagull/icons';
import { useStateFromUrl } from 'seagull/utils/useStateFromUrl';
import AgreementStudio from "../../components/AgreementStudio/AgreementStudio";
import { useFetchAgreementStudioData } from "../../components/AgreementStudio/hooks/useFetchAgreementStudioData";
import { CreateProposalButton } from "../../components/CreateProposalButton/CreateProposalButton";
import { makeSentryIgnoredError } from "../../components/ErrorBoundaryPage";
import ExternalLinkButton from "../../components/ExternalLinkButton";
import PageLoadingState from "../../components/PageLoadingState";
import { ErrorText, routes } from "../../constants";
import { useOverlayContext, useStore, useStoreActions, useStoreStatic } from "../../contexts";
import { useDebouncedLogSearchToBI } from "../../hooks/useDebouncedLogSearchToBI";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { useProposalActions } from "../../hooks/useProposalActions";
import { useSetDocumentTitle } from "../../hooks/useSetDocumentTitle";
import { useSortFromUrl } from "../../hooks/useSortFromUrl";
import { useBulkActionModal } from "../../hooks/useTerminateAgreement/useBulkActionModal";
import { Intent } from "../../infra/access-control";
import { handlePromise } from "../../infra/utils";
import { relationshipSummaryModel } from "../../models";
import { DraftModel } from "../../models/DraftModel";
import { RelationshipStatus } from "../../models/relationship";
import { BusinessChannelEventBinder } from "../../pusher";
import { selectors } from "../../store/state-selectors";
import pluralize from "../../utils/pluralize";
import SeeWhatItsLike from "../../components/SeeWhatItsLike/SeeWhatItsLike";
import ActivityLogModal from "./ActivityLogModal/ActivityLogModal";
import styles from "./ProposalsDashboard.module.scss";
import { sortFunctions, getStatusUIData, getProposalsStatusCounters } from "./ProposalsDashboard.utils";
import ProposalsDashboardRow from "./ProposalsDashboardRow";
import { ProposalsIllustration } from "./ProposalsIllustration";
import { ProposalsProgressStatusFilterPicker } from "./ProposalsProgressStatusFilterPicker";
import { ProposalsSentByFilterPicker } from "./ProposalsSentByFilterPicker/ProposalsSentByFilterPicker";
import img1 from "./SeeWhatItsLikeImages/1.png";
import img2 from "./SeeWhatItsLikeImages/2.png";
import img3 from "./SeeWhatItsLikeImages/3.png";
import img4 from "./SeeWhatItsLikeImages/4.png";
import { WithdrawModal } from "./WithdrawModal/WithdrawModal";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const vendorTitle = 'Sent proposals';
const clientTitle = 'Proposals';
export let HeaderColId = /*#__PURE__*/function (HeaderColId) {
  HeaderColId["CompanyName"] = "companyName";
  HeaderColId["SentDate"] = "sentDate";
  HeaderColId["SentBy"] = "sentBy";
  HeaderColId["Status"] = "status";
  return HeaderColId;
}({});
const getHeaderItems = () => [{
  id: HeaderColId.CompanyName,
  value: 'Company name'
}, {
  id: HeaderColId.SentDate,
  value: 'Sent date'
}, {
  id: HeaderColId.SentBy,
  value: 'Sent by'
}, {
  id: HeaderColId.Status,
  value: 'Status'
}].filter(Boolean);
const getColumnDefs = () => [{
  width: 300,
  sortable: true
},
// Company name
{
  width: [90, 110],
  sortable: true
},
// Sent date
{
  width: [150, 180],
  sortable: true
},
// Sent by
{
  width: [230, 300],
  sortable: true,
  sortOrder: ['asc', 'desc']
} // Status
].filter(Boolean);
const defaultSort = {
  colIndex: 1,
  sort: 'desc'
};
export function ActualProposalsDashboard(props) {
  const {
    data,
    isRefreshing,
    refresh
  } = props;
  const isVendor = useStore(selectors.user.isVendor);
  const overlayContext = useOverlayContext();
  const {
    api,
    logBI,
    fetchLegalTerms
  } = useStoreActions();
  const fetchAgreementStudioData = useFetchAgreementStudioData();
  const {
    getState
  } = useStoreStatic();
  const scrollElRef = useRef(null);
  const [viewProposalMode, setViewProposalMode] = useState(null);
  const columnDefs = useMemo(() => getColumnDefs(), []);
  const headerItems = useMemo(() => getHeaderItems(), []);
  const sortIds = useMemo(() => headerItems.map(item => item.id), [headerItems]);
  const {
    sort,
    changeSort
  } = useSortFromUrl({
    defaultSort,
    sortIds
  });
  const actions = useProposalActions();
  const debouncedLogSearchToBI = useDebouncedLogSearchToBI(11303);
  const {
    doBulkAction
  } = useBulkActionModal();
  const {
    urlState: {
      filter,
      search,
      sentBy
    },
    changeUrlState
  } = useStateFromUrl({
    filter: {
      multi: true,
      defaultValue: []
    },
    search: {
      defaultValue: ''
    },
    // people names may contain commas, so we use a custom query parameter delimeter
    sentBy: {
      multi: true,
      defaultValue: [],
      delimeter: '|'
    }
  });
  const sortedAndFiltered = useMemo(() => {
    const textSearchProposal = (search, proposal) => "".concat(relationshipSummaryModel.opposingPartyName(proposal, isVendor), " ").concat(proposal.title).toLowerCase().includes(search);
    const filtered = data.filter(proposal => {
      const passesStatusFilter = filter.length === 0 || filter.includes(getStatusUIData(proposal, isVendor).viewableStatus);
      const passesSentByFilter = sentBy.length === 0 || sentBy.includes(proposal.seller.id);
      const normalizedSearch = search ? search.trim().toLowerCase() : search;
      const passesSearch = !normalizedSearch || textSearchProposal(normalizedSearch, proposal);
      return passesStatusFilter && passesSentByFilter && passesSearch;
    });
    if (sort) filtered.sort(sortFunctions[sort.colIndex]({
      isVendor,
      isAscending: sort.sort === 'asc'
    }));
    return filtered;
  }, [data, isVendor, sort, filter, search, sentBy]);
  const [selected, setSelected] = useState({});
  const getSelected = useStateInRef(selected);
  const [bulkActionsOpen, setBulkActionsOpen] = useState(false);
  const numSelected = useMemo(() => {
    var _countBy$true;
    return (_countBy$true = countBy(selected).true) !== null && _countBy$true !== void 0 ? _countBy$true : 0;
  }, [selected]);
  const {
    paginatedData,
    onScroll
  } = useInfiniteScroll({
    data: sortedAndFiltered,
    scrollElRef
  });

  //TODO - counters should be received as a separate API
  const counters = useMemo(() => getProposalsStatusCounters(data, isVendor), [data, isVendor]);
  const onSortChange = sort => {
    logBI(11300, {
      column: sort ? headerItems[sort.colIndex].id : 'none'
    });
    changeSort(sort);
  };
  const resetFilters = () => {
    changeUrlState({
      filter: [],
      search: '',
      sentBy: []
    });
  };
  useEffect(() => {
    // this unselects any agreement that is not found in the newly fetched list of agreements
    if (!data || isEmpty(getSelected())) return;
    const ids = data.map(proposal => proposal.id);
    const newSelected = mapValues(getSelected(), (origValue, proposalId) => ids.includes(proposalId) ? origValue : false);
    setSelected(newSelected);
  }, [data]);
  function getSelectedProposals() {
    return Object.entries(selected).filter(_ref => {
      let [, isSelected] = _ref;
      return isSelected;
    }).map(_ref2 => {
      let [proposalId, _] = _ref2;
      return proposalId;
    });
  }
  const onToggleAllSelection = useCallback(selectAll => {
    setSelected(sortedAndFiltered.reduce((acc, _ref3) => {
      let {
        id
      } = _ref3;
      return Object.assign(acc, {
        [id]: selectAll
      });
    }, {}));
  }, [sortedAndFiltered]);
  if (!data.length) {
    return /*#__PURE__*/_jsx(Page, {
      header: isVendor ? vendorTitle : clientTitle,
      loading: isRefreshing,
      children: /*#__PURE__*/_jsx(PageEmptyState, {
        "data-testid": "proposals-dashboard-empty",
        illustration: /*#__PURE__*/_jsx(ProposalsIllustration, {}),
        title: isVendor && 'Create proposals in a matter of minutes',
        message: isVendor ? 'Our proposals are designed for a fast setup with smart fields, a service library, and default settings.' : 'Here you will see proposals pending your approval',
        actions: isVendor && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(SeeWhatItsLike, {
            "data-testid": "proposals-dashboard",
            steps: [{
              title: 'Standardized proposal creation',
              subtitle: 'One proposal creation process for all of your services with easy client searching, a testing option, and a service library.',
              image: {
                src: img1,
                width: 1246,
                height: 748
              }
            }, {
              title: 'Productized services, made easy',
              subtitle: 'Break down your offerings into service modules that can be mixed up for different client cases and easily added to your agreements.',
              image: {
                src: img2,
                width: 1246,
                height: 748
              }
            }, {
              title: 'Get proposals approved quickly!',
              subtitle: 'Show your clients a branded, clear breakdown of your services with pricing so they can easily understand what they need to approve.',
              image: {
                src: img3,
                width: 1198,
                height: 700
              }
            }, {
              title: 'Tracking your proposals made simple',
              subtitle: /*#__PURE__*/_jsxs(_Fragment, {
                children: [/*#__PURE__*/_jsx(Typography, {
                  skin: "invertedStandard",
                  children: "Keep track of the proposal status, edit, and remind a client to approve \u2014 all in one place!"
                }), /*#__PURE__*/_jsx(Typography, {
                  skin: "invertedStandard",
                  children: "We will support you with automatic reminders as well."
                })]
              }),
              image: {
                src: img4,
                width: 1246,
                height: 748
              }
            }]
          }), /*#__PURE__*/_jsx(CreateProposalButton, {
            text: "Create a new proposal",
            hidePrefix: true,
            "data-testid": "agreements-empty-new-btn"
          })]
        })
      })
    });
  }
  const openImportProposalsModal = () => {
    overlayContext.showModal({
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/_jsx(ConfirmationModal, {
          textAlignCenter: false,
          primaryButtonLabel: "Close",
          onPrimaryButtonClick: dismissModal,
          onCloseButtonClick: dismissModal,
          title: "Import proposals",
          primaryButtonTestid: "close-import-proposals-modal",
          content: /*#__PURE__*/_jsxs("div", {
            children: [/*#__PURE__*/_jsx(Typography, {
              children: "Need to send multiple proposals and speed up creation process?"
            }), /*#__PURE__*/_jsxs(Typography, {
              children: [/*#__PURE__*/_jsx(ExternalLinkButton, {
                openInNewTab: true,
                href: "https://forms.gle/Q9sMHXFcp5Fs866FA",
                children: "Complete this form"
              }), ' ', "and our team will reach out with the next steps."]
            }), /*#__PURE__*/_jsx(Typography, {
              variant: "paragraph2",
              style: {
                marginTop: '16px'
              },
              children: "Please note: This service is currently available for requests of 30 proposals or more."
            })]
          })
        });
      }
    });
  };
  async function bulkSendReminders(selectedProposals) {
    await doBulkAction({
      bulkItemIds: selectedProposals,
      confirmation: {
        title: 'Send reminders in bulk',
        primaryButtonLabel: 'Send reminders',
        content: /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsxs(Typography, {
            children: ["By selecting \"Send reminders\", reminders will be sent to all clients of the", ' ', /*#__PURE__*/_jsx("strong", {
              children: selectedProposals.length
            }), " selected proposals."]
          })
        })
      },
      progressTitle: 'Send reminders in bulk',
      actionName: 'sent',
      itemName: 'reminder',
      onConfirmed: () => {
        logBI(11255, {
          count: selectedProposals.length
        });
      },
      canProcessItem: () => true,
      processSingleItem: proposalId => handlePromise(api.relationship.sendProposalApprovalReminder(proposalId)),
      onDone: failedProposalIds => {
        setSelected(failedProposalIds.reduce((acc, proposalId) => _objectSpread(_objectSpread({}, acc), {}, {
          [proposalId]: true
        }), {}));
      }
    });
  }
  function viewActivityLog(proposal) {
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: _ref5 => {
        let {
          dismissModal
        } = _ref5;
        return /*#__PURE__*/_jsx(ActivityLogModal, {
          relationshipId: proposal.id,
          dismissModal: dismissModal
        });
      }
    });
  }
  async function viewProposal(proposal) {
    const worksAt = selectors.user.business(getState());
    const userEmail = selectors.user.email(getState());
    if (!worksAt || !userEmail) return;
    const loader = 'viewProposal';
    overlayContext.startLoader(loader);
    const result = await handlePromise(Promise.all([api.relationship.get(proposal.id), fetchLegalTerms().then(terms => terms ? terms.map(term => term.id) : Promise.reject(makeSentryIgnoredError('legalTermsError'))), fetchAgreementStudioData()]));
    overlayContext.stopLoader(loader);
    if (result.success) {
      const [relationship, legalTermsDocIds] = result.data;
      const proposalAsDraft = DraftModel.fromRelationship(relationship, legalTermsDocIds);
      setViewProposalMode({
        proposalAsDraft,
        duplicate: actions.getDuplicateProposalAction(proposal.id, {
          isSampleProposal: proposal.isSample,
          draftId: proposal.draftId
        }),
        copyLink: actions.getCopyProposalLinkAction(proposal),
        sendReminder: actions.getSendProposalReminderAction(proposal.id, proposal.isSample),
        edit: actions.getEditProposalAction(proposal, 'viewProposal'),
        cancel: {
          text: 'Withdraw',
          intent: Intent.CancelProposal,
          disabled: false,
          onClick: () => overlayContext.showModal({
            closeOnClickOutside: true,
            closeOnEscapeKey: true,
            content: _ref6 => {
              let {
                dismissModal
              } = _ref6;
              return /*#__PURE__*/_jsx(WithdrawModal, {
                openedBy: "agreement-studio",
                relationshipId: relationship.id,
                draftId: relationship.draftId,
                dismissModal: dismissModal,
                onSuccess: refresh
              });
            }
          })
        }
      });
    } else {
      overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain);
    }
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(BusinessChannelEventBinder, {
      source: "scout",
      eventName: PusherEventName.RelationshipCreatedFromScheduledDraft,
      callback: refresh
    }), /*#__PURE__*/_jsxs(Page, {
      "data-testid": "proposals-dashboard",
      ref: scrollElRef,
      header: isVendor ? vendorTitle : clientTitle,
      actions: isVendor && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Button, {
          skin: "primaryOutline",
          "data-testid": "import-proposals-btn",
          onClick: () => openImportProposalsModal(),
          children: "Import"
        }), /*#__PURE__*/_jsx(CreateProposalButton, {})]
      }),
      subheader: "".concat(pluralize('sent proposal', data.filter(proposal => proposal.status !== RelationshipStatus.Expired).length, true)),
      loading: isRefreshing,
      onScroll: onScroll,
      children: [!!viewProposalMode && /*#__PURE__*/_jsx(AgreementStudio, {
        disableDataFetching: true,
        viewProposalMode: viewProposalMode,
        returnRoute: routes.proposalsDashboard,
        onClose: () => setViewProposalMode(null)
      }), /*#__PURE__*/_jsxs(TableList.Provider, {
        hasActionColumn: true,
        columnDefs: columnDefs,
        sort: sort,
        onSortChange: onSortChange,
        enableRowSelection: isVendor,
        selectedRows: selected,
        onSelectionChange: setSelected,
        numRows: sortedAndFiltered.length,
        numSelectedRows: numSelected,
        onToggleAllSelection: onToggleAllSelection,
        children: [/*#__PURE__*/_jsxs(PageContentHeader, {
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.filtersRow,
            children: [/*#__PURE__*/_jsxs(FilterPickerGroup, {
              "data-testid": "proposals-dashboard-filter-picker-group",
              onClearClick: resetFilters,
              disableClear: !filter.length && !search && !sentBy.length,
              children: [/*#__PURE__*/_jsx(Searchbox, {
                registerHotkey: true,
                value: search,
                "data-testid": "proposals-dashboard-search",
                onChange: event => {
                  const searchStr = event.target.value;
                  changeUrlState({
                    search: searchStr
                  });
                  debouncedLogSearchToBI.onChange(searchStr);
                },
                onClear: () => {
                  changeUrlState({
                    search: ''
                  });
                  debouncedLogSearchToBI.onClear();
                }
              }), /*#__PURE__*/_jsx(ProposalsProgressStatusFilterPicker, {
                isVendor: isVendor,
                value: filter,
                onChange: filter => changeUrlState({
                  filter
                }),
                counters: counters
              }), /*#__PURE__*/_jsx(ProposalsSentByFilterPicker, {
                value: sentBy,
                onChange: sentBy => changeUrlState({
                  sentBy
                }),
                possibleValues: data.reduce((acc, _ref7) => {
                  let {
                    seller: {
                      id,
                      firstName,
                      lastName
                    }
                  } = _ref7;
                  return firstName != null && lastName != null ? Object.assign(acc, {
                    [id]: "".concat(firstName, " ").concat(lastName)
                  }) : acc;
                }, {})
              })]
            }), isVendor && /*#__PURE__*/_jsxs("div", {
              className: styles.bulkActions,
              children: [/*#__PURE__*/_jsx(Typography, {
                "data-testid": "agreement-dashboard-bulk-counter",
                children: numSelected > 0 ? "".concat(pluralize('proposal', numSelected, true), " selected") : ''
              }), /*#__PURE__*/_jsx(DropdownMenu, {
                onOpenChange: setBulkActionsOpen,
                target: /*#__PURE__*/_jsx(ChipButton, {
                  "data-testid": "proposals-dashboard-bulk-actions",
                  suffixIcon: bulkActionsOpen ? /*#__PURE__*/_jsx(WideArrowUpward, {}) : /*#__PURE__*/_jsx(WideArrowDownward, {}),
                  highlight: bulkActionsOpen,
                  disabled: numSelected === 0,
                  children: "Bulk actions"
                }),
                children: /*#__PURE__*/_jsx(DropdownMenu.Item, {
                  iconStart: /*#__PURE__*/_jsx(MailSentLiner24, {}),
                  "data-testid": "proposals-dashboard-bulk-send-reminders",
                  onClick: () => bulkSendReminders(getSelectedProposals()),
                  disabled: !isVendor,
                  children: "Send reminders"
                })
              })]
            })]
          }), /*#__PURE__*/_jsx(TableList.Header, {
            items: headerItems,
            "data-testid": "proposals-dashboard-table-header"
          })]
        }), sortedAndFiltered.length > 0 ? /*#__PURE__*/_jsx(_Fragment, {
          children: paginatedData.map((proposal, index) => /*#__PURE__*/_jsx(ProposalsDashboardRow, {
            proposal: proposal,
            isVendor: isVendor,
            index: index,
            refresh: refresh,
            onViewActivityLog: viewActivityLog,
            onViewProposal: viewProposal,
            proposalActions: actions
          }, proposal.id))
        }) : /*#__PURE__*/_jsx(PageEmptyState, {
          illustration: /*#__PURE__*/_jsx(ProposalsIllustration, {}),
          title: "No Proposals match your request",
          message: "Try adjusting your search by removing filters.",
          actions: /*#__PURE__*/_jsx(Button, {
            onClick: resetFilters,
            children: "Remove all filters"
          })
        })]
      })]
    })]
  });
}
export function ProposalsDashboard() {
  const {
    data,
    loading,
    error
  } = useStore(state => state.proposals);
  const isVendor = useStore(selectors.user.isVendor);
  const {
    fetchProposals
  } = useStoreActions();
  useSetDocumentTitle(isVendor ? vendorTitle : clientTitle);
  useEffect(() => {
    fetchProposals();
  }, [isVendor]);
  if (error) {
    return /*#__PURE__*/_jsx(PageErrorState, {
      title: isVendor ? vendorTitle : clientTitle,
      onRefresh: fetchProposals
    });
  } else if (!data) {
    return /*#__PURE__*/_jsx(PageLoadingState, {
      header: isVendor ? vendorTitle : clientTitle,
      loading: loading
    });
  } else {
    return /*#__PURE__*/_jsx(ActualProposalsDashboard, {
      data: data,
      isRefreshing: loading,
      refresh: fetchProposals
    });
  }
}